package com.taocent.simple.compose.component.richtext.core.internal.editor

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.TextUnit
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.emoji.CUSTOM_EMOJI_PLACEHOLDER
import com.taocent.simple.compose.component.richtext.core.internal.emoji.CUSTOM_EMOJI_TAG

internal class TextEditor(private val state: RichTextState) {

    private var preCompositionValue: TextFieldValue? = null

    fun onValueChange(newValue: TextFieldValue, supportsImeComposition: Boolean = true) {
        val oldValue = state.textFieldValue

        if (!supportsImeComposition && newValue.composition != null) {
            // iOS / 旧版 Android 平台:BasicTextField 走非 IME composition 通道,
            // onValueChange 给的 newValue.annotatedString 通常只有 text 变化、不携带 spans
            // (BasicTextField 内部 IME state 不复制 state 的 spans)。
            // 修复前:这里直接 state.updateTextFieldValue(newValue) 会把 state 的 spans 整体清空,
            // 造成用户报告的"开始 IME 输入 → 拼音预选上去 → 前面文本样式全部丢失"。
            // 修复:用 rebuildForNonImePlatform 重建 AnnotatedString,以 newValue 作 spans 源、
            // 缺失时 fallback 到 state.currentSpanStyle / 旧 spans(prefix+suffix);
            // 再调 syncParagraphModelsAfterTextChange 保证 paragraph styles(lineHeight /
            // textAlign / textIndent)也同步,避免"IME commit 后字看起来变小"的现象。
            preCompositionValue = null
            val compRange = newValue.composition!!
            val rebuilt = rebuildForNonImePlatform(oldValue, newValue)
            val withParagraph = state.syncParagraphModelsAfterTextChange(
                oldText = oldValue.annotatedString.text,
                newAnnotated = rebuilt,
                editStart = compRange.start,
                removedCount = compRange.end - compRange.start,
                insertedText = newValue.annotatedString.text,
                fallbackTextAlign = state.currentTextAlign
            )
            state.updateTextFieldValue(
                TextFieldValue(
                    annotatedString = withParagraph,
                    selection = newValue.selection,
                    composition = newValue.composition
                )
            )
            return
        }

        if (!supportsImeComposition && oldValue.composition != null) {
            // iOS / 旧版 Android 平台:IME commit 阶段(从 composition 切到 final)。
            // 同样地,BasicTextField 给的 newValue.annotatedString 可能没有 spans。
            // 修复:用 rebuildForNonImePlatform 重建,保留 prefix 段(IME composition 起始位置之前)
            // 的旧 spans,并把 committed 字符按 currentSpanStyle 补 spans;
            // 再调 syncParagraphModelsAfterTextChange 同步 paragraph styles,避免 commit 后
            // paragraph styles(lineHeight / textAlign)被丢失,视觉上像"字体变小"。
            preCompositionValue = null
            val compRange = oldValue.composition!!
            val rebuilt = rebuildForNonImePlatform(oldValue, newValue)
            val withParagraph = state.syncParagraphModelsAfterTextChange(
                oldText = oldValue.annotatedString.text,
                newAnnotated = rebuilt,
                editStart = compRange.start,
                removedCount = compRange.end - compRange.start,
                insertedText = newValue.annotatedString.text,
                fallbackTextAlign = state.currentTextAlign
            )
            state.justToggledStyle = false
            state.updateTextFieldValue(
                TextFieldValue(
                    annotatedString = withParagraph,
                    selection = newValue.selection
                )
            )
            state.syncStyleFromSelection()
            return
        }

        if (newValue.composition != null) {
            handleCompositionInput(oldValue, newValue)
            return
        }

        if (oldValue.composition != null) {
            handleCompositionCommit(oldValue, newValue)
            return
        }

        handleNormalInput(oldValue, newValue)
    }

    /**
     * 在 `!supportsImeComposition` 平台(iOS)上重建 AnnotatedString。
     *
     * BasicTextField 在这种平台上给的 newValue.annotatedString 只有 text/selection/composition,
     ** 不携带 state 的 spans(IME 通道不读 state 维护的 spans)。直接 set 会把 spans 清空。
     *
     * 这里用与 [processTextChange] / [handleCompositionInput] 相同的策略重建:
     * - 优先用 newValue.annotatedString 对应区间的 spans(BasicTextField 可能传了部分 spans)
     * - 缺失时 fallback 到 state 旧 spans(prefix / suffix)
     * - 仍缺失时(新字符)用 state.currentSpanStyle() 补
     */
    private fun rebuildForNonImePlatform(
        oldValue: TextFieldValue,
        newValue: TextFieldValue,
    ): AnnotatedString {
        val oldAnnotated = oldValue.annotatedString
        val newAnnotated = newValue.annotatedString
        val newText = newAnnotated.text
        val oldText = oldAnnotated.text
        val newLen = newAnnotated.length
        val oldLen = oldAnnotated.length

        // text 没变(只 selection 变):直接保留旧 spans
        if (oldText == newText) return oldAnnotated

        // 简化处理:把 newValue 视为"在 oldValue 基础上做了一次纯文本替换",
        // 用 oldValue.spans + newValue.text 重建。
        // 找出 oldText 与 newText 的最长公共前缀/后缀,
        // prefix/suffix 段直接用 newAnnotated 对应区间(它继承了 BasicTextField 内部
        // 的 spans) + fallback 到 oldAnnotated,中间插入/删除段按 currentSpanStyle 补。
        val commonPrefixLen = run {
            var i = 0
            val max = minOf(oldLen, newLen)
            while (i < max && oldText[i] == newText[i]) i++
            i
        }
        val commonSuffixLen = run {
            var i = 0
            val max = minOf(oldLen - commonPrefixLen, newLen - commonPrefixLen)
            while (i < max && oldText[oldLen - 1 - i] == newText[newLen - 1 - i]) i++
            i
        }
        val oldKeepStart = commonPrefixLen
        val oldKeepEnd = oldLen - commonSuffixLen
        val newKeepStart = commonPrefixLen
        val newKeepEnd = newLen - commonSuffixLen

        val builder = AnnotatedString.Builder()
        if (commonPrefixLen > 0) {
            // prefix 段:用 newAnnotated 的 spans(可能有 BasicTextField 传的部分 spans),
            // 没 spans 时 fallback 到 oldAnnotated
            val newPrefix = newAnnotated.subSequence(0, newKeepStart)
            if (newPrefix.spanStyles.isEmpty() && newPrefix.paragraphStyles.isEmpty()) {
                builder.append(oldAnnotated.subSequence(0, oldKeepStart))
            } else {
                builder.append(newPrefix)
            }
        }
        // 中间段(被插入/替换的部分):
        // 用 newAnnotated 对应区间(新字符),缺失时按 currentSpanStyle 补。
        if (newKeepStart < newKeepEnd) {
            val middleNew = newAnnotated.subSequence(newKeepStart, newKeepEnd)
            if (middleNew.spanStyles.isEmpty() && middleNew.paragraphStyles.isEmpty()) {
                builder.pushStyle(state.currentSpanStyle())
                builder.append(newText.substring(newKeepStart, newKeepEnd))
                builder.pop()
            } else {
                builder.append(middleNew)
            }
        }
        if (commonSuffixLen > 0) {
            val newSuffixStart = newLen - commonSuffixLen
            val newSuffix = newAnnotated.subSequence(newSuffixStart, newLen)
            if (newSuffix.spanStyles.isEmpty() && newSuffix.paragraphStyles.isEmpty()) {
                builder.append(oldAnnotated.subSequence(oldLen - commonSuffixLen, oldLen))
            } else {
                builder.append(newSuffix)
            }
        }
        return builder.toAnnotatedString()
    }

    fun insertText(text: String) {
        val selection = state.textFieldValue.selection
        val start = selection.min
        val end = selection.max
        val annotated = state.textFieldValue.annotatedString
        val builder = AnnotatedString.Builder()
        if (start > 0) builder.append(annotated.subSequence(0, start))
        builder.pushStyle(state.currentSpanStyle())
        builder.append(text)
        builder.pop()
        if (end < annotated.length) builder.append(annotated.subSequence(end, annotated.length))
        val rawAnnotated = builder.toAnnotatedString()
        val newAnnotated = state.syncParagraphModelsAfterTextChange(
            oldText = annotated.text,
            newAnnotated = rawAnnotated,
            editStart = start,
            removedCount = end - start,
            insertedText = text,
            fallbackTextAlign = state.currentTextAlign
        )
        state.justToggledStyle = false
        state.updateTextFieldValue(
            TextFieldValue(
                annotatedString = newAnnotated,
                selection = TextRange(start + text.length)
            )
        )
        state.syncStyleFromSelection()
    }

    fun deleteSelection() {
        val selection = state.textFieldValue.selection
        if (selection.collapsed) return
        val start = selection.min
        val end = selection.max
        val annotated = state.textFieldValue.annotatedString
        val builder = AnnotatedString.Builder()
        if (start > 0) builder.append(annotated.subSequence(0, start))
        if (end < annotated.length) builder.append(annotated.subSequence(end, annotated.length))
        val rawAnnotated = builder.toAnnotatedString()
        val newAnnotated = state.syncParagraphModelsAfterTextChange(
            oldText = annotated.text,
            newAnnotated = rawAnnotated,
            editStart = start,
            removedCount = end - start,
            insertedText = ""
        )
        state.justToggledStyle = false
        state.updateTextFieldValue(
            TextFieldValue(
                annotatedString = newAnnotated,
                selection = TextRange(start)
            )
        )
        state.syncStyleFromSelection()
    }

    fun insertAnnotatedString(annotated: AnnotatedString) {
        val selection = state.textFieldValue.selection
        val start = selection.min
        val end = selection.max
        val current = state.textFieldValue.annotatedString
        val builder = AnnotatedString.Builder()
        if (start > 0) builder.append(current.subSequence(0, start))
        builder.append(annotated)
        if (end < current.length) builder.append(current.subSequence(end, current.length))
        val rawAnnotated = builder.toAnnotatedString()
        val newAnnotated = state.syncParagraphModelsAfterTextChange(
            oldText = current.text,
            newAnnotated = rawAnnotated,
            editStart = start,
            removedCount = end - start,
            insertedText = annotated.text,
            fallbackTextAlign = state.currentTextAlign
        )
        state.justToggledStyle = false
        state.updateTextFieldValue(
            TextFieldValue(
                annotatedString = newAnnotated,
                selection = TextRange(start + annotated.length)
            )
        )
        state.syncStyleFromSelection()
    }

    fun insertTextWithEmojis(text: String, validEmojiIds: Set<String>, displaySize: TextUnit) {
        val pattern = Regex("""\[([a-zA-Z0-9_]+)\]""")
        val matches = pattern.findAll(text).toList()
        val validMatches = matches.filter { it.groupValues[1] in validEmojiIds }
        if (validMatches.isEmpty()) {
            insertText(text)
            return
        }
        val selection = state.textFieldValue.selection
        val start = selection.min
        val end = selection.max
        val current = state.textFieldValue.annotatedString
        val builder = AnnotatedString.Builder()
        if (start > 0) builder.append(current.subSequence(0, start))
        val baseStyle = state.currentSpanStyle()
        var lastEnd = 0
        for (match in validMatches) {
            if (match.range.first > lastEnd) {
                builder.append(text.substring(lastEnd, match.range.first))
            }
            val emojiId = match.groupValues[1]
            val emojiStart = builder.length
            val emojiStyle = baseStyle.copy(
                fontSize = displaySize,
                color = Color.Transparent
            )
            builder.pushStyle(emojiStyle)
            builder.append(CUSTOM_EMOJI_PLACEHOLDER)
            builder.pop()
            builder.addStringAnnotation(CUSTOM_EMOJI_TAG, emojiId, emojiStart, builder.length)
            lastEnd = match.range.last + 1
        }
        if (lastEnd < text.length) {
            builder.append(text.substring(lastEnd))
        }
        if (end < current.length) builder.append(current.subSequence(end, current.length))
        val rawAnnotated = builder.toAnnotatedString()
        val insertedText = rawAnnotated.text.substring(start, rawAnnotated.length - (current.length - end))
        val newAnnotated = state.syncParagraphModelsAfterTextChange(
            oldText = current.text,
            newAnnotated = rawAnnotated,
            editStart = start,
            removedCount = end - start,
            insertedText = insertedText,
            fallbackTextAlign = state.currentTextAlign
        )
        state.justToggledStyle = false
        state.updateTextFieldValue(
            TextFieldValue(
                annotatedString = newAnnotated,
                selection = TextRange(start + newAnnotated.length - (if (start > 0) start else 0) - (current.length - end))
            )
        )
        state.syncStyleFromSelection()
    }

    fun insertCustomEmoji(emojiId: String, displaySize: TextUnit) {
        val selection = state.textFieldValue.selection
        val start = selection.min
        val end = selection.max
        val current = state.textFieldValue.annotatedString
        val builder = AnnotatedString.Builder()
        if (start > 0) builder.append(current.subSequence(0, start))
        val emojiStart = builder.length
        val baseStyle = if (state.justToggledStyle) {
            state.currentSpanStyle()
        } else {
            val isAfterHyperlink = start > 0 && current
                .getStringAnnotations(RichTextState.HYPERLINK_TAG, start - 1, start)
                .isNotEmpty()
            val leftStyle = if (!isAfterHyperlink && start > 0) {
                current.spanStyles
                    .lastOrNull { it.start <= start - 1 && it.end > start - 1 }
                    ?.item
            } else null
            leftStyle ?: state.currentSpanStyle()
        }
        val emojiStyle = baseStyle.copy(
            fontSize = if (state.justToggledStyle) {
                displaySize
            } else {
                baseStyle.fontSize.takeIf { it != TextUnit.Unspecified } ?: displaySize
            },
            color = Color.Transparent
        )
        builder.pushStyle(emojiStyle)
        builder.append(CUSTOM_EMOJI_PLACEHOLDER)
        builder.pop()
        val emojiEnd = builder.length
        builder.addStringAnnotation(CUSTOM_EMOJI_TAG, emojiId, emojiStart, emojiEnd)
        if (end < current.length) builder.append(current.subSequence(end, current.length))
        val rawAnnotated = builder.toAnnotatedString()
        val newAnnotated = state.syncParagraphModelsAfterTextChange(
            oldText = current.text,
            newAnnotated = rawAnnotated,
            editStart = start,
            removedCount = end - start,
            insertedText = CUSTOM_EMOJI_PLACEHOLDER,
            fallbackTextAlign = state.currentTextAlign
        )
        state.justToggledStyle = false
        state.updateTextFieldValue(
            TextFieldValue(
                annotatedString = newAnnotated,
                selection = TextRange(emojiEnd)
            )
        )
        state.syncStyleFromSelection()
    }

    private fun handleCompositionInput(oldValue: TextFieldValue, newValue: TextFieldValue) {
        if (oldValue.composition == null) {
            preCompositionValue = oldValue
        }
        val snapshot = preCompositionValue ?: oldValue
        val paragraphSnapshot = state.paragraphModels.map { it.copy() }
        val comp = newValue.composition!!
        val styled = snapshot.annotatedString
        val newOverallLen = newValue.annotatedString.text.length
        val compLen = comp.end - comp.start
        val nonCompNewLen = newOverallLen - compLen
        if (nonCompNewLen < styled.length) {
            val delStart = comp.start
            val delEnd = comp.start + (styled.length - nonCompNewLen)
            val overlappingLinks = styled.getStringAnnotations(RichTextState.HYPERLINK_TAG, delStart, delEnd)
            if (overlappingLinks.isNotEmpty()) {
                val link = overlappingLinks.first()
                val oldSel = snapshot.selection
                if (oldSel.collapsed || oldSel.min > link.start || oldSel.max < link.end) {
                    preCompositionValue = null
                    state.updateTextFieldValue(
                        snapshot.copy(
                            selection = TextRange(link.start, link.end),
                            composition = null
                        )
                    )
                    return
                }
            }
        }
        // 修复:计算"已经从 snapshot 文本中实际删除的字符数",作为 suffix 起点。
        // 背景:用户在有选区时输入 IME 字符,BasicTextField 给的 newValue.text 长度
        // = styled.length - selectionLen + compLen(删选区 + 插 comp 字符)。
        // 之前的代码用 `prefixLen = min(comp.start, styled.length)` 作为 suffix 起点,
        // 这只在 selection collapsed 时才正确。当 selection 非 collapsed 时,
        // suffix 应该从"删除选区之后"的位置开始,即 `prefixLen + selectionLen`。
        val snapshotSel = snapshot.selection
        val selectionLen = if (snapshotSel.collapsed) 0 else snapshotSel.max - snapshotSel.min
        val prefixLen = minOf(comp.start, styled.length)
        // 真正的 suffix 在 styled 中的起点 = prefixLen + 被删除字符数(从 styled 中)。
        // 当 IME 起始位置 = selection.min 且 compText 长度 = selectionLen 时,
        // styled 中被删的位置 = [prefixLen, prefixLen + selectionLen]。
        val baseSuffixStartInStyled = minOf(prefixLen + selectionLen, styled.length)
        val newAnnotated = newValue.annotatedString
        val newLen = newAnnotated.length
        val builder = AnnotatedString.Builder()
        if (prefixLen > 0) {
            // 修复:用 newValue.annotatedString 作 prefix spans 源(由 BasicTextField 维护,
            // 含从 state 继承的 spans + IME 字符);
            // 仅当 newValue 对应区间没有 span 覆盖时,fallback 到 preComp.annotatedString,
            // 避免 IME composition 阶段把 prefix 的 spans 丢失(典型场景:用户从工具栏设置
            // 字号/颜色等 span style 后立刻 IME 输入,prefix 段 spans 必须保留)。
            val newPrefix = newAnnotated.subSequence(0, minOf(prefixLen, newLen))
            if (newPrefix.spanStyles.isEmpty() && newPrefix.paragraphStyles.isEmpty()) {
                builder.append(styled.subSequence(0, prefixLen))
            } else {
                builder.append(newPrefix)
            }
        }
        val compText = newValue.annotatedString.text.substring(comp.start, comp.end)
        if (compText.isNotEmpty()) {
            // 修复:composition 字符优先用 newValue 对应区间的 spans(可能继承自 state),
            // 没 span 覆盖才用 state.currentSpanStyle() 兜底(普通字符继承当前光标位置样式)。
            val newComp = newAnnotated.subSequence(
                comp.start.coerceAtMost(newLen), comp.end.coerceAtMost(newLen)
            )
            if (newComp.spanStyles.isEmpty() && newComp.paragraphStyles.isEmpty()) {
                builder.pushStyle(state.currentSpanStyle())
                builder.append(compText)
                builder.pop()
            } else {
                builder.append(newComp)
            }
        }
        if (baseSuffixStartInStyled < styled.length) {
            // 修复:有 selection 时,suffix 起点要跳过被删除的 selection 范围。
            val newSuffixStart = minOf(comp.end, newLen)
            val newSuffix = newAnnotated.subSequence(newSuffixStart, newLen)
            if (newSuffix.spanStyles.isEmpty() && newSuffix.paragraphStyles.isEmpty()) {
                builder.append(styled.subSequence(baseSuffixStartInStyled, styled.length))
            } else {
                builder.append(newSuffix)
            }
        }
        val rawResultAnnotated = builder.toAnnotatedString()
        val resultAnnotated = state.syncParagraphModelsAfterTextChange(
            oldText = styled.text,
            newAnnotated = rawResultAnnotated,
            editStart = prefixLen,
            removedCount = selectionLen,
            insertedText = compText,
            fallbackTextAlign = state.currentTextAlign
        )
        val newSelection = state.snapCursorSelection(resultAnnotated, snapshot.selection, newValue.selection)
        state.updateTextFieldValue(
            TextFieldValue(
                annotatedString = resultAnnotated,
                selection = newSelection,
                composition = newValue.composition
            )
        )
        state.restoreParagraphModels(paragraphSnapshot)
    }

    private fun handleCompositionCommit(oldValue: TextFieldValue, newValue: TextFieldValue) {
        val preComp = preCompositionValue ?: oldValue
        preCompositionValue = null
        val comp = oldValue.composition!!
        val styledAnnotated = preComp.annotatedString
        val prefixLen = minOf(comp.start, styledAnnotated.length)
        val newText = newValue.annotatedString.text
        val newAnnotated = newValue.annotatedString
        val newLen = newAnnotated.length
        // 修复:计算 preComp 时的 selection 长度(在 IME 输入开始时是否已选中),
        // 用于 suffix 起点跳过被删除的 selection 范围。
        // 背景:用户在有选区时输入 IME 字符,BasicTextField 给的 newValue.text 长度
        // = styledAnnotated.length - selectionLen + committedLen(删选区 + 替换为 commit 字符)。
        val preCompSel = preComp.selection
        val selectionLen = if (preCompSel.collapsed) 0 else preCompSel.max - preCompSel.min
        val committedLen = newText.length - (styledAnnotated.length - prefixLen) + selectionLen
        val committedText = newText.substring(prefixLen, committedLen.coerceAtLeast(prefixLen))
        val baseSuffixStartInStyled = minOf(prefixLen + selectionLen, styledAnnotated.length)
        val builder = AnnotatedString.Builder()
        if (prefixLen > 0) {
            // 修复:用 newValue.annotatedString 作 prefix spans 源,缺失时 fallback 到 preComp。
            // 见 handleCompositionInput 的注释。
            val newPrefix = newAnnotated.subSequence(0, minOf(prefixLen, newLen))
            if (newPrefix.spanStyles.isEmpty() && newPrefix.paragraphStyles.isEmpty()) {
                builder.append(styledAnnotated.subSequence(0, prefixLen))
            } else {
                builder.append(newPrefix)
            }
        }
        if (committedText.isNotEmpty()) {
            // 修复:committed 字符优先用 newValue 对应区间的 spans。
            val committedStart = prefixLen
            val committedEnd = (prefixLen + committedText.length).coerceAtMost(newLen)
            val newCommitted = newAnnotated.subSequence(committedStart, committedEnd)
            if (newCommitted.spanStyles.isEmpty() && newCommitted.paragraphStyles.isEmpty()) {
                builder.pushStyle(state.currentSpanStyle())
                builder.append(committedText)
                builder.pop()
            } else {
                builder.append(newCommitted)
            }
        }
        if (baseSuffixStartInStyled < styledAnnotated.length) {
            // 修复:有 selection 时,suffix 起点要跳过被删除的 selection 范围。
            val newSuffixStart = minOf(prefixLen + committedText.length, newLen)
            val newSuffix = newAnnotated.subSequence(newSuffixStart, newLen)
            if (newSuffix.spanStyles.isEmpty() && newSuffix.paragraphStyles.isEmpty()) {
                builder.append(styledAnnotated.subSequence(baseSuffixStartInStyled, styledAnnotated.length))
            } else {
                builder.append(newSuffix)
            }
        }
        val rawResulting = builder.toAnnotatedString()
        val resulting = state.syncParagraphModelsAfterTextChange(
            oldText = styledAnnotated.text,
            newAnnotated = rawResulting,
            editStart = prefixLen,
            removedCount = selectionLen,
            insertedText = committedText,
            fallbackTextAlign = state.currentTextAlign
        )
        val sel = TextRange(prefixLen + committedText.length)
        val newSelection = state.snapCursorSelection(resulting, preComp.selection, sel)
        val committedValue = TextFieldValue(
            annotatedString = resulting,
            selection = newSelection
        )
        state.recordUndoSnapshot(preComp, committedValue)
        state.skipNextUndoRecord = true
        state.justToggledStyle = false
        state.updateTextFieldValue(committedValue)
        state.syncStyleFromSelection()
    }

    private fun handleNormalInput(oldValue: TextFieldValue, newValue: TextFieldValue) {
        val oldText = oldValue.annotatedString.text
        val newText = newValue.annotatedString.text

        if (oldText == newText) {
            val newSelection = state.snapCursorSelection(oldValue.annotatedString, oldValue.selection, newValue.selection)
            state.updateTextFieldValue(oldValue.copy(selection = newSelection))
            state.syncStyleFromSelection()
            return
        }

        val oldSel = oldValue.selection
        val newLen = newText.length
        val oldLen = oldText.length
        val delStart: Int
        val delEnd: Int
        val insCount: Int
        if (newLen < oldLen) {
            if (oldSel.collapsed) {
                val deletedCount = oldLen - newLen
                if (newValue.selection.min < oldSel.min) {
                    delStart = maxOf(0, oldSel.min - deletedCount)
                    delEnd = oldSel.min
                } else {
                    delStart = oldSel.min
                    delEnd = minOf(oldSel.min + deletedCount, oldLen)
                }
            } else {
                delStart = oldSel.min
                delEnd = oldSel.max
            }
            // 修复:计算实际的"删除 + 插入"语义。
            // 旧代码把 insCount 一律设为 -1,导致 processTextChange 走"只删除"分支,
            // 丢失 newText 中实际的插入内容(用户在有选区时输入英文的情况)。
            // 实际插入字符数 = newLen - (oldLen - (delEnd - delStart))
            insCount = newLen - (oldLen - (delEnd - delStart))

            if (delStart < delEnd) {
                val overlappingLinks = oldValue.annotatedString
                    .getStringAnnotations(RichTextState.HYPERLINK_TAG, delStart, delEnd)
                if (overlappingLinks.isNotEmpty()) {
                    val link = overlappingLinks.first()
                    if (oldSel.collapsed ||
                        oldSel.min > link.start ||
                        oldSel.max < link.end
                    ) {
                        state.updateTextFieldValue(
                            oldValue.copy(
                                selection = TextRange(link.start, link.end)
                            )
                        )
                        return
                    }
                }

                val overlappingEmojis = oldValue.annotatedString
                    .getStringAnnotations(CUSTOM_EMOJI_TAG, delStart, delEnd)
                if (oldSel.collapsed && overlappingEmojis.isNotEmpty()) {
                    val emoji = overlappingEmojis.first()
                    val annotated = oldValue.annotatedString
                    val builder = AnnotatedString.Builder()
                    if (emoji.start > 0) builder.append(annotated.subSequence(0, emoji.start))
                    if (emoji.end < annotated.length) builder.append(annotated.subSequence(emoji.end, annotated.length))
                    val rawAnnotated = builder.toAnnotatedString()
                    val resultAnnotated = state.syncParagraphModelsAfterTextChange(
                        oldText = annotated.text,
                        newAnnotated = rawAnnotated,
                        editStart = emoji.start,
                        removedCount = emoji.end - emoji.start,
                        insertedText = ""
                    )
                    state.updateTextFieldValue(
                        TextFieldValue(
                            annotatedString = resultAnnotated,
                            selection = TextRange(emoji.start)
                        )
                    )
                    return
                }
            }
        } else {
            delStart = -1
            delEnd = -1
            insCount = newLen - oldLen
        }

        val annotated = processTextChange(
            oldValue.annotatedString, newValue, delStart, delEnd, insCount, oldSel
        )
        val shouldKeepTrailingEmptyLineAlign =
            newLen < oldLen &&
                newText.endsWith('\n') &&
                newValue.selection.collapsed &&
                newValue.selection.start == newText.length &&
                oldText.lastIndexOf('\n') < delStart
        val previousTextAlign = state.currentTextAlign
        val intermediate = TextFieldValue(
            annotatedString = annotated,
            selection = newValue.selection,
            composition = newValue.composition
        )
        state.justToggledStyle = false
        state.updateTextFieldValue(state.snapCursorOutOfHyperlinks(oldValue.selection, intermediate))
        if (shouldKeepTrailingEmptyLineAlign) {
            state.currentTextAlign = previousTextAlign
        } else {
            state.syncStyleFromSelection()
        }
    }

    private fun processTextChange(
        oldAnnotated: AnnotatedString,
        newValue: TextFieldValue,
        delStart: Int,
        delEnd: Int,
        insCount: Int,
        oldSelection: TextRange
    ): AnnotatedString {
        val oldLen = oldAnnotated.length
        val newText = newValue.annotatedString.text
        val newAnnotated = newValue.annotatedString
        val builder = AnnotatedString.Builder()

        // 修复:即使是"删除 + 插入"情况(delStart >= 0 且 insCount > 0),
        // 也需要走第二个分支处理"删除选区 + 插入新字符"的语义,
        // 而不是第一个分支(只删除,不插入)。
        // 背景:用户在有选区时输入英文(无 IME composition),handleNormalInput 会
        // 把 delStart/delEnd 设为选区范围,insCount 设为 -1(因为 newLen < oldLen),
        // 导致 processTextChange 走第一个分支,**完全丢失 newText 的插入内容**。
        // 典型现象:文本 "aaabbb" 选中 "aaa" 输入 "c" → 期望 "cbbb",实际 "bbb"。
        if (insCount <= 0 && delStart >= 0 && delEnd > delStart) {
            if (delStart > 0) builder.append(oldAnnotated.subSequence(0, delStart))
            if (delEnd < oldLen) builder.append(oldAnnotated.subSequence(delEnd, oldLen))
            val rawAnnotated = builder.toAnnotatedString()
            return state.syncParagraphModelsAfterTextChange(
                oldText = oldAnnotated.text,
                newAnnotated = rawAnnotated,
                editStart = delStart,
                removedCount = delEnd - delStart,
                insertedText = ""
            )
        }

        val removeStart = oldSelection.min
        val removeEnd = oldSelection.max
        if (removeStart > 0) builder.append(oldAnnotated.subSequence(0, removeStart))
        if (insCount > 0) {
            val newPartEnd = minOf(removeStart + insCount, newText.length)
            val newPartStart = minOf(removeStart, newPartEnd)
            val newPart = newText.substring(newPartStart, newPartEnd)
            if (newPart.isNotEmpty()) {
                // 修复前:这里只 pushStyle(state.currentSpanStyle()) 给新字符,
                // 若 newValue.annotatedString 自带的 spans 缺失或 currentSpanStyle 推断错误
                // (典型场景:首次写入富文本,paste 进来的 JSON,跨 state 复制粘贴等),
                // 新字符会丢失样式 — 整个 AnnotatedString 重建为 Empty SpanStyle。
                //
                // 修复后:用 newValue.annotatedString 中对应区间作为 baseline;
                // 仅当该区间完全没有 span 覆盖时,才用 state.currentSpanStyle() 补 pushStyle。
                // newValue.annotatedString 由 BasicTextField 维护,包含从 state 继承的 spans
                // + IME 新增字符的当前 spans,是最可靠的源。
                val newPartAnnotated = newAnnotated.subSequence(newPartStart, newPartEnd)
                if (newPartAnnotated.spanStyles.isEmpty() &&
                    newPartAnnotated.paragraphStyles.isEmpty()
                ) {
                    builder.pushStyle(state.currentSpanStyle())
                    builder.append(newPart)
                    builder.pop()
                } else {
                    builder.append(newPartAnnotated)
                }
            }
        }
        if (removeEnd < oldLen) builder.append(oldAnnotated.subSequence(removeEnd, oldLen))
        val rawAnnotated = builder.toAnnotatedString()
        val insertedText = if (insCount > 0) {
            val newPartEnd = minOf(removeStart + insCount, newText.length)
            newText.substring(minOf(removeStart, newPartEnd), newPartEnd)
        } else {
            ""
        }
        return state.syncParagraphModelsAfterTextChange(
            oldText = oldAnnotated.text,
            newAnnotated = rawAnnotated,
            editStart = removeStart,
            removedCount = removeEnd - removeStart,
            insertedText = insertedText,
            fallbackTextAlign = state.currentTextAlign
        )
    }
}
